// SessionGuard.jsx
// Shared "existing session" handling for every login page (CARA Admin, CARA User,
// Superadmin, TPRM Admin, TPRM User). Centralizes:
//   1. Detecting a session already active in this browser (client-side check).
//   2. Detecting a session conflict reported by the backend (403 existing_session).
//   3. Invalidating that existing server session and clearing client-side cache
//      before letting the user continue with a fresh login.
// Extracted so the 5 login pages don't each re-implement the same state,
// effects and modal markup.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { checkExistingSession, normalizeRole } from './sessionChecker';
import { clearAllSessionData } from '../../utils/logoutHandler';
import { HELPDESK_EMAIL } from '../../config/supportContacts';

const API_URL = process.env.REACT_APP_API_URL;

// The 4 core portals (Cara Admin, Cara User, TPRM Admin, TPRM User) share a
// single active-session policy: a session for ANY one of them blocks login
// on the other 3. Superadmin/VRM are intentionally not part of this group.
export const SINGLE_SESSION_ROLES = ['CARAA', 'CARA', 'TPRM', 'TPRMUSER'];

// Canonical post-login destination for each role, reused so "Continue with
// Current Session" / a resolved conflict lands the user on their own role's
// real destination regardless of which portal's login page surfaced it.
const ROLE_DASHBOARD_ROUTES = {
  CARAA: '/ota',
  CARA: '/otp?user_type=CARA',
  TPRM: '/otp?user_type=TPRM',
  TPRMUSER: '/otp?user_type=TPRMUSER',
  SUPERADMIN: '/otp?user_type=SUPERADMIN',
  VRM: '/vrm_admin_dashboard',
};

export function getDashboardRouteForRole(role) {
  return ROLE_DASHBOARD_ROUTES[normalizeRole(role)] || '/assessment_dashboard';
}

/**
 * Lightweight client-side browser name detection, stored to localStorage at
 * login so the "Active Session Detected" modal (a purely client-side,
 * localStorage-driven check) can show which device/browser the active
 * session belongs to - the login response itself doesn't return this.
 */
export function getBrowserName() {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Unknown';
}

/**
 * @param {string|string[]} [expectedRole] - Role(s) belonging to this login
 *   page's single-session-policy group (e.g. SINGLE_SESSION_ROLES for the 4
 *   core portals). Restricts the "active session" check to that group so
 *   each policy group only surfaces sessions that belong to it.
 */
export function useSessionGuard(expectedRole) {
  const { t } = useTranslation('session_guard');
  const [existingSession, setExistingSession] = useState(null);
  const [showExistingSessionModal, setShowExistingSessionModal] = useState(false);
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLogoutButton, setShowLogoutButton] = useState(false);
  // Structured existing_session payload from a backend 403 conflict (as opposed
  // to `existingSession`, which comes from this browser's own localStorage).
  // Used so "Logout Existing Session" can target the OLD session's own
  // username/session_id even when it belongs to a completely different
  // account on another portal/device.
  const [conflictSession, setConflictSession] = useState(null);
  // Single-use token the backend mints alongside a 403 conflict response,
  // proving this browser just supplied the correct password for the
  // account being kicked out. Required by /logout/'s logout_existing_only
  // mode — without it, that endpoint would accept a bare username from
  // anyone (see F04 in the pentest report).
  const [conflictToken, setConflictToken] = useState(null);

  // Check for a session already active in this browser (localStorage-based),
  // scoped to this login page's own role(s).
  useEffect(() => {
    const checkSession = async () => {
      try {
        const sessionCheck = await checkExistingSession(expectedRole);
        if (sessionCheck.hasSession) {
          setExistingSession(sessionCheck.userData);
          setShowExistingSessionModal(true);
        }
      } catch (err) {
        console.warn('Session check failed:', err);
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the error modal in sync with the error state.
  useEffect(() => {
    setShowErrorModal(!!error);
  }, [error]);

  /**
   * Call from a login catch-block. Detects the backend's 403 "already logged
   * in from another device/browser" response and surfaces it as a session
   * conflict. Returns true if it handled the error (caller should stop),
   * false otherwise (caller should continue with normal error handling).
   */
  const handleSessionConflict = (err) => {
    if (
      err.response?.status === 403 &&
      (err.response.data?.message?.includes("already logged in") || err.response.data?.existing_session)
    ) {
      const session = err.response.data.existing_session;
      let errorMsg = err.response.data.message;
      if (session) {
        // F11 FIX: the backend no longer sends device/browser/ip/session_id
        // (session internals shouldn't leave the HttpOnly cookie channel) -
        // only show what it still returns.
        errorMsg += `\n\nExisting Session Details:\n` +
          `Role: ${session.role || 'Unknown'}\n` +
          `Login Time: ${session.login_time || 'Unknown'}`;
      }
      setError(errorMsg);
      setShowLogoutButton(true);
      setConflictSession(session || null);
      setConflictToken(err.response.data?.conflict_token || null);
      return true;
    }
    setShowLogoutButton(false);
    setConflictSession(null);
    setConflictToken(null);
    return false;
  };

  /**
   * "Logout Existing Session" action:
   *   1. Invalidate the existing server-side session (oldest active/pending one).
   *   2. Clear all client-side cached/session data on THIS browser.
   *   3. Let the caller continue with the new login attempt.
   *
   * `username` is the caller's best-guess fallback (usually the new-login
   * form's typed username). When a backend 403 conflict already told us the
   * OLD session's own username (`conflictSession`, which may belong to a
   * different account on a different portal entirely), that takes priority -
   * targeting the wrong account would silently no-op the logout.
   */
  const logoutExistingSession = async (username) => {
    const targetUsername = conflictSession?.username || username;
    try {
      await axios.post(`${API_URL}api/logout/`, {
        username: targetUsername,
        logout_existing_only: true,
        conflict_token: conflictToken
      }, { withCredentials: true });

      // Server-side session is invalidated — now clear this browser's cache
      // so no stale identity/session data lingers before the new login.
      clearAllSessionData();

      setError("");
      setShowLogoutButton(false);
      setShowErrorModal(false);
      setConflictSession(null);
      setConflictToken(null);

      return { success: true, message: t('msg.existingSessionLoggedOut', { defaultValue: 'Existing session has been logged out. Please try logging in again.' }) };
    } catch (err) {
      const message = err.response?.data?.message || t('msg.logoutExistingFailed', { defaultValue: 'Failed to logout existing session. Please try again.' });
      setError(message);
      return { success: false, message };
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setError("");
  };

  return {
    existingSession,
    showExistingSessionModal,
    setShowExistingSessionModal,
    error,
    setError,
    showErrorModal,
    showLogoutButton,
    setShowLogoutButton,
    handleSessionConflict,
    logoutExistingSession,
    closeErrorModal,
    conflictSession,
  };
}

/**
 * "You are already logged in on this browser" modal — shown on page load
 * when localStorage still holds a previous session (e.g. browser was closed
 * without logging out).
 */
export function ExistingSessionModal({ show, existingSession, onLogout, onContinue }) {
  const { t } = useTranslation('session_guard');
  if (!show || !existingSession) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-warning text-dark">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {t('modal.existingSession.title', { defaultValue: 'Active Session Detected' })}
              </h5>
            </div>
            <div className="modal-body text-center">
              <div className="mb-3">
                <i className="fas fa-user-check fa-3x text-warning mb-3"></i>
                <h6>{t('modal.existingSession.heading', { defaultValue: 'You are already logged in!' })}</h6>
                <p className="text-muted">
                  {t('label.user', { defaultValue: 'User:' })} <strong>{existingSession.username}</strong><br />
                  {t('label.role', { defaultValue: 'Role:' })} <strong>{existingSession.role}</strong>
                  {existingSession.deviceType && (<><br />{t('label.device', { defaultValue: 'Device:' })} <strong>{existingSession.deviceType}</strong></>)}
                  {existingSession.browser && (<><br />{t('label.browser', { defaultValue: 'Browser:' })} <strong>{existingSession.browser}</strong></>)}
                </p>
                <p className="text-info small">
                  {t('modal.existingSession.hint', { defaultValue: 'Please logout from your current session before logging in again.' })}
                </p>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button type="button" className="btn btn-secondary me-2" onClick={onLogout}>
                <i className="fas fa-sign-out-alt me-1"></i>
                {t('btn.logoutAndLoginAgain', { defaultValue: 'Logout & Login Again' })}
              </button>
              <button type="button" className="btn btn-primary" onClick={onContinue}>
                <i className="fas fa-arrow-right me-1"></i>
                {t('btn.continueWithCurrentSession', { defaultValue: 'Continue with Current Session' })}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

/**
 * "Session Already Active" / generic login-error modal — shown when the
 * backend rejects the login (either a real session conflict on another
 * device/browser, or any other login error).
 */
export function SessionConflictModal({ show, error, showLogoutButton, onClose, onLogoutExisting }) {
  const { t } = useTranslation('session_guard');
  if (!show || !error) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {showLogoutButton ? t('modal.conflict.titleConflict', { defaultValue: 'Session Conflict' }) : t('modal.conflict.titleError', { defaultValue: 'Login Error' })}
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label={t('btn.close', { ns: 'common', defaultValue: 'Close' })}></button>
            </div>
            <div className="modal-body text-center">
              <div className="mb-3">
                <i className="fas fa-exclamation-circle fa-3x text-danger mb-3"></i>
                <h6>{showLogoutButton ? t('modal.conflict.headingActive', { defaultValue: 'Session Already Active' }) : t('modal.conflict.headingFailed', { defaultValue: 'Login Failed' })}</h6>
                <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                  {error}
                </p>
                {showLogoutButton && (
                  <p className="text-info small mt-2">
                    {t('modal.conflict.hint', { defaultValue: 'You can logout from the existing session to continue.' })}
                  </p>
                )}
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              {showLogoutButton && (
                <button type="button" className="btn btn-secondary me-2" onClick={onLogoutExisting}>
                  <i className="fas fa-sign-out-alt me-1"></i>
                  {t('btn.logoutExistingSession', { defaultValue: 'Logout Existing Session' })}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={onClose}>
                <i className="fas fa-times me-1"></i>
                {t('btn.close', { ns: 'common', defaultValue: 'Close' })}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

// Formats a "YYYY-MM-DD" string as "14 Aug 2026" without shifting the day
// across timezones (a plain `new Date(str)` parses as UTC midnight, which
// can render as the previous day in timezones behind UTC).
function formatSuspendedUntil(dateStr) {
  const parts = String(dateStr).split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts.map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const SUPPORT_EMAIL = HELPDESK_EMAIL;

/**
 * Account Suspended / Account Deactivated modal, shown when the backend
 * rejects login with status "DEACTIVATED". `suspendedUntil` (a "YYYY-MM-DD"
 * string) distinguishes a time-boxed suspension from a permanent
 * deactivation and switches between the two designs.
 */
export function AccountRestrictedModal({ show, suspendedUntil, onClose }) {
  const { t } = useTranslation('session_guard');
  if (!show) return null;

  const isSuspended = Boolean(suspendedUntil);
  const accent = isSuspended
    ? { header: 'linear-gradient(135deg, #fee2e2, #fecaca)', text: '#dc2626', soft: '#fee2e2', badge: '#dc2626' }
    : { header: 'linear-gradient(135deg, #dbeafe, #bfdbfe)', text: '#2563eb', soft: '#dbeafe', badge: '#2563eb' };

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            {/* Header */}
            <div
              className="modal-header border-0"
              style={{ background: accent.header, padding: '1rem 1.25rem' }}
            >
              <h5 className="modal-title fw-bold mb-0 d-flex align-items-center" style={{ color: '#1f2937' }}>
                <i className={`fas ${isSuspended ? 'fa-user-lock' : 'fa-user-xmark'} me-2`} style={{ color: accent.text }}></i>
                {isSuspended
                  ? t('modal.deactivated.titleSuspended', { defaultValue: 'Account Suspended' })
                  : t('modal.deactivated.title', { defaultValue: 'Account Deactivated' })}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label={t('btn.close', { ns: 'common', defaultValue: 'Close' })}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body p-4 text-center">
              <div className="mb-4 d-flex justify-content-center">
                <div
                  className="d-flex align-items-center justify-content-center position-relative"
                  style={{ width: '110px', height: '110px', borderRadius: '50%', background: accent.soft }}
                >
                  <i
                    className={`fas ${isSuspended ? 'fa-lock' : 'fa-user'}`}
                    style={{ fontSize: '2.75rem', color: accent.text }}
                  ></i>
                  <span
                    className="d-flex align-items-center justify-content-center position-absolute"
                    style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      background: accent.badge,
                      bottom: '4px',
                      right: '2px',
                      border: '2px solid #fff',
                    }}
                  >
                    <i className={`fas ${isSuspended ? 'fa-pause' : 'fa-xmark'} text-white`} style={{ fontSize: '0.9rem' }}></i>
                  </span>
                </div>
              </div>

              <h4 className="fw-bold mb-3">
                {isSuspended
                  ? t('modal.deactivated.headingSuspended', { defaultValue: 'Login Temporarily Restricted' })
                  : t('modal.deactivated.heading', { defaultValue: 'Login Restricted' })}
              </h4>

              {isSuspended ? (
                <p className="mb-3" style={{ fontSize: '1.05rem' }}>
                  {t('modal.deactivated.messagePrefix', { defaultValue: 'Your account has been' })}{' '}
                  <strong>{t('modal.deactivated.suspendedWord', { defaultValue: 'suspended' })}</strong>{' '}
                  {t('modal.deactivated.suspendedUntil', { defaultValue: 'until' })}
                  <br />
                  <span
                    className="d-inline-flex align-items-center gap-2 fw-bold mt-2"
                    style={{
                      background: accent.soft,
                      color: accent.text,
                      border: `1px solid ${accent.text}55`,
                      borderRadius: '999px',
                      padding: '0.4rem 1rem',
                    }}
                  >
                    <i className="fas fa-calendar-alt"></i>
                    {formatSuspendedUntil(suspendedUntil)}
                  </span>
                </p>
              ) : (
                <p className="text-muted mb-3" style={{ fontSize: '1.05rem' }}>
                  {t('modal.deactivated.messagePrefix', { defaultValue: 'Your account has been' })}{' '}
                  <strong>{t('modal.deactivated.deactivatedWord', { defaultValue: 'deactivated' })}</strong>{' '}
                  {t('modal.deactivated.messageSuffixNew', {
                    defaultValue: 'and access to {{product}} is currently unavailable.',
                    product: 'CARAgrc',
                  })}
                </p>
              )}

              <div className="d-flex align-items-start gap-3 text-start p-3 rounded-4 mb-1" style={{ background: '#eff6ff' }}>
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#2563eb' }}
                >
                  <i className="fas fa-envelope text-white" style={{ fontSize: '0.9rem' }}></i>
                </span>
                <p className="mb-0 text-dark">
                  {isSuspended
                    ? t('modal.deactivated.contactSuspended', { defaultValue: 'If you believe this suspension is incorrect or need assistance, please contact your administrator or our support team at' })
                    : t('modal.deactivated.contactReactivation', { defaultValue: 'To request account reactivation, please contact your administrator or our support team at' })}
                  {' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="fw-bold" style={{ color: '#2563eb' }}>{SUPPORT_EMAIL}</a>.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer border-0 p-3 pt-0 justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                style={{ borderRadius: '10px' }}
                onClick={onClose}
              >
                {t('btn.close', { ns: 'common', defaultValue: 'Close' })}
              </button>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="btn text-white px-4 d-flex align-items-center gap-2"
                style={{ borderRadius: '10px', background: '#2563eb' }}
              >
                <i className="fas fa-headset"></i>
                {t('btn.contactSupport', { defaultValue: 'Contact Support' })}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
